import { Group, Stack, useMantineTheme } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import type { IconNames } from '@repo/foundations';
import { Icon, Text, Title } from '@repo/foundations';
import { isEmpty } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import type { IBaseModel, IIntegration } from '../../api';
import {
	integrationsQueryKeyFactory,
	queryClient,
	useCreateDatabuilderJob,
	useDeleteIntegration,
	useUpdateIntegration,
} from '../../api';
import type { ExtendedDataTableColumn } from '../../components/TableV2/types';

import type { ICommandListItem } from '../../components/Spotlight/components/CommandPalette/constants';

import { EntityType } from '@repo/common/enums/entityType';
import {
	incidentsQueryKeyFactory,
	monitorsQueryKeyFactory,
} from '../../api/hooks/monitoring/constants.ts';
import {
	getIdentityTokenIfOidc,
	shouldCancelSyncForOidc,
} from '../../components/Integration/identityTokenHelpers';
import { IntegrationToggle } from '../../components/Integration/IntegrationToggle.tsx';
import IntegrationLogo from '../../components/IntegrationLogo';
import { useDeleteManyAction } from '../../components/TableV2/BaseModel.hooks';
import {
	IntegrationStatusRender,
	RelativeTimeRender,
	TeamRender,
} from '../../components/TableV2/render';
import { DatabuilderJobType } from '../../interfaces';
import { formatIntegrationType } from '../../utils/stringUtils';

export function useColumns<
	T extends IIntegration,
>(): ExtendedDataTableColumn<T>[] {
	const theme = useMantineTheme();

	const { mutateAsync: updateIntegration } = useUpdateIntegration({
		disableOptimisticUpdate: false,

		invalidationKeys: [],
		options: {
			onSuccess: (data, variables) => {
				// always invalidate the specific integration's detail view
				queryClient.invalidateQueries({
					queryKey: integrationsQueryKeyFactory.byId(variables.data.id),
					exact: true,
				});

				const changedFields = Object.keys(variables.data);
				const listAffectingFields = new Set([
					'name',
					'status',
					'last_run_at',
					'scheduled_extractions_cron',
					'scheduled_push_metadata_cron',
				]);

				const listDisplayFields = new Set([
					'enabled',
					'next_run_at',
					'scheduled_extractions_next_run',
					'scheduled_push_metadata_next_run',
				]);

				const shouldInvalidateLists = changedFields.some((field) =>
					listAffectingFields.has(field)
				);

				const hasListDisplayChanges = changedFields.some((field) =>
					listDisplayFields.has(field)
				);

				if (shouldInvalidateLists) {
					queryClient.invalidateQueries({
						queryKey: integrationsQueryKeyFactory.allLists(),
					});
				} else if (hasListDisplayChanges) {
					// For display-only changes, update the specific integration in all list caches
					queryClient.setQueriesData(
						{ queryKey: integrationsQueryKeyFactory.allLists() },
						(oldData: any) => {
							if (!oldData) return oldData;

							return {
								...oldData,
								results: oldData.results?.map((integration: any) =>
									integration.id === variables.data.id
										? { ...integration, ...data }
										: integration
								),
							};
						}
					);
				}
			},
		},
	});

	const handleChangeIntegration = useCallback(
		(field: string) => (id: string) => (value: unknown) =>
			updateIntegration({
				data: {
					id,
					[field]: value,
				},
			}),
		[updateIntegration]
	);

	const columns: ExtendedDataTableColumn<T>[] = useMemo(
		() => [
			{
				accessor: 'name',
				titleLabel: 'Name',
				render: (record: IIntegration) => (
					<Group role="link" spacing="xs" noWrap>
						<IntegrationLogo
							integrationId={record.id}
							integrationType={record.type}
							width={theme.other.space[4]}
							height={theme.other.space[4]}
						/>
						<Stack spacing={0}>
							<Title size="sm" truncate>
								{isEmpty(record.name) ? 'Untitled' : record.name}
							</Title>
							<Text size="xs" color="text/secondary/default">
								{formatIntegrationType(record.type)}
							</Text>
						</Stack>
					</Group>
				),
				width: 'auto',
			},
			{
				accessor: 'status',
				titleLabel: 'Last sync status',
				render: (record: IIntegration) => (
					<IntegrationStatusRender record={record} />
				),
			},
			{
				accessor: 'scheduled_extractions_last_run',
				titleLabel: 'Last sync',
				render: (record: IIntegration) => (
					<RelativeTimeRender
						record={record}
						field={'scheduled_extractions_last_run'}
					/>
				),
			},
			{
				accessor: 'scheduled_extractions_next_run',
				titleLabel: 'Next sync',
				render: (record: IIntegration) => {
					// Only show next sync time if a schedule is set
					if (
						!record.scheduled_extractions_cron ||
						record.scheduled_extractions_cron.trim().length === 0
					) {
						return null;
					}
					return (
						<RelativeTimeRender
							record={record}
							field={'scheduled_extractions_next_run'}
							isFuture
						/>
					);
				},
			},
			{
				navigate: false,
				accessor: 'teams',
				titleLabel: 'Teams',
				render: (record: IIntegration) => (
					<TeamRender
						record={record}
						onChange={handleChangeIntegration('teams')}
					/>
				),
			},
			{
				accessor: 'enabled',
				titleLabel: 'State',
				navigate: false,
				render: (record: IIntegration) => (
					<IntegrationToggle
						enabled={record.enabled}
						onChange={handleChangeIntegration('enabled')(record.id)}
					/>
				),
				width: 70,
			},
		],
		[handleChangeIntegration, theme.other.space]
	);

	return columns;
}

export const useActions = (): ICommandListItem<IIntegration>[] => {
	const { mutateAsync: createDatabuilderJob } = useCreateDatabuilderJob({});
	const { mutateAsync: deleteIntegration } = useDeleteIntegration({});
	const onDelete = useCallback(
		(id: string) => deleteIntegration({ id }),
		[deleteIntegration]
	);
	const { action } = useDeleteManyAction<IIntegration>({
		name: 'integration',
		queryKeys: integrationsQueryKeyFactory.allLists(),
		onDelete,
	});

	const handleRunExtractions = useCallback(
		async (selected: IIntegration[]) => {
			const isRunning = selected.some(
				(integration) =>
					integration.status === 'running' || integration.status === 'queued'
			);

			if (isRunning) {
				showNotification({
					title: 'Integrations are queued or running',
					message:
						'Some of the selected integrations are queued or running. Please wait for the integrations to finish running to run sync again',
					color: 'red',
					icon: (
						<Icon name="alertCircle" color="text/critical-on-fill/default" />
					),
				});
				return;
			}

			// Process each integration, checking for OIDC
			const promises = selected.map(async (integration) => {
				const identityToken = await getIdentityTokenIfOidc({
					integration,
				});

				const { shouldCancel, error } = shouldCancelSyncForOidc(identityToken, {
					integration,
					integrationName: integration.name,
				});

				if (shouldCancel) {
					return Promise.reject(error);
				}

				return createDatabuilderJob({
					data: {
						integration_id: integration.id,
						type: DatabuilderJobType.METADATA,
						...(identityToken && { identity_token: identityToken }),
					},
				});
			});

			try {
				await Promise.all(promises);
				showNotification({
					title: 'Integrations queued for running',
					message: 'The integrations have been queued for running',
					color: 'green',
					icon: <Icon name="check" color="text/success-on-fill/default" />,
				});

				// ENG-10149 Invalidate the queries for monitors and incidents
				await queryClient.invalidateQueries({
					queryKey: monitorsQueryKeyFactory.allLists(),
				});
				await queryClient.invalidateQueries({
					queryKey: incidentsQueryKeyFactory.allLists(),
				});
			} catch (e) {
				showNotification({
					title: 'Error running integrations',
					message: 'If the issue persists, please contact support',
					color: 'red',
					icon: (
						<Icon name="alertCircle" color="text/critical-on-fill/default" />
					),
				});
			}
		},
		[createDatabuilderJob]
	);

	const actions = useMemo(
		() => [
			{
				id: 'actions::run',
				title: 'Run sync',
				name: 'Run',
				quickAction: { label: 'Run Run sync', value: true },
				iconName: 'playerPlay' as IconNames,
				hotkey: '/mp',
				type: EntityType.integration,
				team: undefined,
				category: 'actions',
				show: true,
				onClick: handleRunExtractions as (
					selected: IBaseModel[]
				) => Promise<void>,
			},
			action,
		],
		[action, handleRunExtractions]
	);

	return actions;
};
